/**
 * TenantClientDbSetup — karta tenanta czekającego na bazę na serwerze klienta (ADR-0113b).
 *
 * Tenant w trybie „serwer klienta” powstaje bez bazy (`db_status = 'awaiting_db'`). Tu wdrożeniowiec
 * przechodzi kroki po kolei: instalacja u klienta (instrukcja + skrypt) → dane serwera (wpis
 * w rejestrze serwerów, podpięty do tenanta) → przygotuj serwer → sprawdź połączenie → utwórz bazę.
 * Kroki 2–4 używają wspólnych okien serwera bazy (`components/db-servers/`) i tras `/api/admin/db-servers`.
 */

import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Circle, CircleDot } from 'lucide-react';

import { Alert } from '@/components/Alert';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { DbServerChecksList } from '@/components/db-servers/DbServerChecksList';
import { DbServerFormModal } from '@/components/db-servers/DbServerFormModal';
import { InstallGuideModal } from '@/components/db-servers/InstallGuideModal';
import { PrepareServerModal } from '@/components/db-servers/PrepareServerModal';
import { Skeleton } from '@/components/Skeleton';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import {
  DB_SERVERS_KEY,
  type DbServerCheckResult,
  type DbServerDto,
  dbServerErrorInfo,
  useCheckDbServer,
} from '@/lib/db-servers/api';

export interface TenantClientDbSetupProps {
  tenantId: string;
  tenantName: string;
  /** Serwer klienta wybrany dla tenanta (krok 2); null = jeszcze nie podany. */
  pendingDbServerId: string | null;
  /** Nieudana próba zostawiła bazę na serwerze klienta do ręcznego usunięcia. */
  orphanDb?: boolean;
}

type StepState = 'done' | 'current' | 'todo';

const PROVISION_ERROR_KEY: Record<string, string> = {
  NO_DB_SERVER: 'admin.tenants.detail.clientDb.errNoServer',
  NOT_AWAITING_DB: 'admin.tenants.detail.clientDb.errNotAwaiting',
  DB_SERVER_UNAVAILABLE: 'admin.tenants.detail.clientDb.errServerUnavailable',
  DB_SERVER_UNREACHABLE: 'admin.tenants.detail.clientDb.errServerUnreachable',
};

function StepIcon({ state }: { state: StepState }) {
  if (state === 'done')
    return <CheckCircle2 size={18} className="text-green-600 dark:text-green-400" aria-hidden />;
  if (state === 'current')
    return <CircleDot size={18} className="text-accent-600 dark:text-accent-400" aria-hidden />;
  return <Circle size={18} className="text-text-muted" aria-hidden />;
}

function Step({
  n,
  title,
  state,
  stateLabel,
  children,
  testId,
}: {
  n: number;
  title: string;
  state: StepState;
  /** Stan kroku słownie — dla czytnika ekranu (ikona i kolor są tylko wizualne). */
  stateLabel: string;
  children?: React.ReactNode;
  testId: string;
}) {
  return (
    <li
      data-testid={testId}
      data-state={state}
      aria-current={state === 'current' ? 'step' : undefined}
      className={`rounded-lg border px-4 py-3 ${
        state === 'current' ? 'border-accent-400 dark:border-accent-600' : 'border-border-secondary'
      }`}
    >
      <div className="flex items-center gap-2">
        <StepIcon state={state} />
        <span
          className={`text-sm font-medium ${state === 'todo' ? 'text-text-muted' : 'text-text-primary'}`}
        >
          {n}. {title}
          <span className="sr-only"> — {stateLabel}</span>
        </span>
      </div>
      {state !== 'todo' && children && <div className="mt-2 pl-7">{children}</div>}
    </li>
  );
}

export function TenantClientDbSetup({
  tenantId,
  tenantName,
  pendingDbServerId,
  orphanDb = false,
}: TenantClientDbSetupProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [guideOpen, setGuideOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [prepareOpen, setPrepareOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [checkResult, setCheckResult] = useState<DbServerCheckResult | null>(null);
  // Serwer zapisany w rejestrze, ale podpięcie do tenanta się nie udało — ponawiamy podpięcie
  // zamiast zakładać drugi wpis (osobnego ekranu serwerów, gdzie dałoby się go posprzątać, nie ma).
  const [unlinkedServerId, setUnlinkedServerId] = useState<string | null>(null);
  const titleId = useId();

  const tenantKey = ['admin', 'tenant', tenantId];

  const serverQuery = useQuery<DbServerDto>({
    enabled: !!pendingDbServerId,
    queryKey: [...DB_SERVERS_KEY, 'item', pendingDbServerId],
    queryFn: async () =>
      (await api.get<{ item: DbServerDto }>(`/admin/db-servers/${pendingDbServerId}`)).data.item,
    retry: false,
  });
  const server = pendingDbServerId ? (serverQuery.data ?? null) : null;

  const linkServer = useMutation({
    mutationFn: async (dbServerId: string) =>
      api.put(`/admin/tenants/${tenantId}/pending-db-server`, { dbServerId }),
    onSuccess: () => {
      setUnlinkedServerId(null);
      void queryClient.invalidateQueries({ queryKey: tenantKey });
    },
    onError: (err, dbServerId) => {
      const code = dbServerErrorInfo(err).code;
      if (code === 'ORPHAN_DB') {
        toast.show({ variant: 'error', title: t('admin.tenants.detail.clientDb.errOrphanLink') });
        return;
      }
      setUnlinkedServerId(dbServerId);
      toast.show({ variant: 'error', title: t('admin.tenants.detail.clientDb.errLink') });
      if (code === 'NOT_AWAITING_DB') {
        void queryClient.invalidateQueries({ queryKey: tenantKey });
      }
    },
  });

  const check = useCheckDbServer();

  const provision = useMutation({
    mutationFn: async () => api.post(`/admin/tenants/${tenantId}/provision-db`),
    onSuccess: () => {
      setConfirmOpen(false);
      toast.show({ variant: 'success', title: t('admin.tenants.detail.clientDb.provisionDone') });
      void queryClient.invalidateQueries({ queryKey: tenantKey });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'tenants'] });
      void queryClient.invalidateQueries({ queryKey: DB_SERVERS_KEY });
    },
    onError: (err) => {
      setConfirmOpen(false);
      const { code, message } = dbServerErrorInfo(err);
      const key =
        (code && PROVISION_ERROR_KEY[code]) ?? 'admin.tenants.detail.clientDb.errProvision';
      toast.show({ variant: 'error', title: t(key), description: message ?? undefined });
      void queryClient.invalidateQueries({ queryKey: tenantKey });
    },
  });

  const serverLoading = !!pendingDbServerId && serverQuery.isLoading;
  const serverLoadFailed = !!pendingDbServerId && serverQuery.isError;
  const serverGone =
    serverLoadFailed &&
    (serverQuery.error as { response?: { status?: number } } | null)?.response?.status === 404;
  const stateLabel = (st: StepState) => t(`admin.tenants.detail.clientDb.state.${st}`);

  const hasServer = !!server;
  const prepared = server?.status === 'active';
  const checked = prepared && server?.lastCheckOk === true;

  const s1: StepState = hasServer ? 'done' : 'current';
  const s2: StepState = hasServer ? 'done' : 'current';
  const s3: StepState = !hasServer ? 'todo' : prepared ? 'done' : 'current';
  const s4: StepState = !prepared ? 'todo' : checked ? 'done' : 'current';
  const s5: StepState = checked ? 'current' : 'todo';

  function runCheck() {
    if (!server) return;
    check.mutate(server.id, {
      onSuccess: (data) => {
        setCheckResult(data.result);
        void queryClient.invalidateQueries({ queryKey: [...DB_SERVERS_KEY, 'item', server.id] });
      },
      onError: () =>
        toast.show({ variant: 'error', title: t('admin.tenants.detail.clientDb.errCheck') }),
    });
  }

  return (
    <Card padding="md" data-testid="tenant-client-db-setup">
      <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
        <h3 id={titleId} className="text-sm font-semibold text-text-primary">
          {t('admin.tenants.detail.clientDb.title')}
        </h3>
        <Badge color="amber">{t('admin.tenants.detail.dbStatus.awaiting_db')}</Badge>
      </div>
      <p className="mb-4 text-sm text-text-secondary">{t('admin.tenants.detail.clientDb.intro')}</p>

      {orphanDb && (
        <Alert
          tone="warning"
          className="mb-3"
          title={t('admin.tenants.detail.clientDb.orphanTitle')}
          data-testid="client-db-orphan"
        >
          {t('admin.tenants.detail.clientDb.orphanText')}
        </Alert>
      )}

      {serverLoadFailed && (
        <Alert
          tone="error"
          className="mb-3"
          data-testid="client-db-server-load-error"
          actions={
            !serverGone && (
              <Button variant="secondary" size="sm" onClick={() => void serverQuery.refetch()}>
                {t('admin.tenants.detail.clientDb.retry')}
              </Button>
            )
          }
        >
          {t(
            serverGone
              ? 'admin.tenants.detail.clientDb.errServerGone'
              : 'admin.tenants.detail.clientDb.errServerLoad',
          )}
        </Alert>
      )}

      {serverLoading ? (
        <div className="flex flex-col gap-3" data-testid="client-db-loading">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} height={44} />
          ))}
        </div>
      ) : (
        <ol className="flex flex-col gap-3" aria-labelledby={titleId}>
          <Step
            n={1}
            state={s1}
            stateLabel={stateLabel(s1)}
            title={t('admin.tenants.detail.clientDb.step1')}
            testId="client-db-step-1"
          >
            <p className="mb-2 text-xs text-text-secondary">
              {t('admin.tenants.detail.clientDb.step1Hint')}
            </p>
            <Button variant="secondary" size="sm" onClick={() => setGuideOpen(true)}>
              {t('admin.tenants.detail.clientDb.openGuide')}
            </Button>
          </Step>

          <Step
            n={2}
            state={s2}
            stateLabel={stateLabel(s2)}
            title={t('admin.tenants.detail.clientDb.step2')}
            testId="client-db-step-2"
          >
            {server ? (
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <span className="text-text-primary">
                  {server.name}{' '}
                  <span className="font-mono text-xs text-text-muted">
                    {server.host}:{server.port}
                  </span>
                </span>
                <Button variant="ghost" size="sm" onClick={() => setFormOpen(true)}>
                  {t('admin.tenants.detail.clientDb.edit')}
                </Button>
              </div>
            ) : (
              <>
                <p className="mb-2 text-xs text-text-secondary">
                  {t('admin.tenants.detail.clientDb.step2Hint')}
                </p>
                {unlinkedServerId ? (
                  <div className="flex flex-col gap-2" data-testid="client-db-relink">
                    <p className="text-xs text-text-secondary">
                      {t('admin.tenants.detail.clientDb.unlinkedHint')}
                    </p>
                    <div>
                      <Button
                        size="sm"
                        onClick={() => linkServer.mutate(unlinkedServerId)}
                        loading={linkServer.isPending}
                      >
                        {t('admin.tenants.detail.clientDb.relink')}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button
                    size="sm"
                    onClick={() => setFormOpen(true)}
                    loading={linkServer.isPending}
                  >
                    {t('admin.tenants.detail.clientDb.addServer')}
                  </Button>
                )}
              </>
            )}
          </Step>

          <Step
            n={3}
            state={s3}
            stateLabel={stateLabel(s3)}
            title={t('admin.tenants.detail.clientDb.step3')}
            testId="client-db-step-3"
          >
            {prepared ? (
              <p className="text-xs text-text-secondary">
                {t('admin.tenants.detail.clientDb.step3Done')}
              </p>
            ) : (
              <>
                <p className="mb-2 text-xs text-text-secondary">
                  {t('admin.tenants.detail.clientDb.step3Hint')}
                </p>
                <Button size="sm" onClick={() => setPrepareOpen(true)}>
                  {t('admin.tenants.detail.clientDb.prepare')}
                </Button>
              </>
            )}
          </Step>

          <Step
            n={4}
            state={s4}
            stateLabel={stateLabel(s4)}
            title={t('admin.tenants.detail.clientDb.step4')}
            testId="client-db-step-4"
          >
            <div className="flex flex-col gap-2">
              {server?.lastCheckOk === false && server.lastCheckError && (
                <p className="text-xs text-red-600 dark:text-red-400">{server.lastCheckError}</p>
              )}
              {checked && (
                <p className="text-xs text-text-secondary">
                  {t('admin.tenants.detail.clientDb.step4Done')}
                </p>
              )}
              <div>
                <Button
                  size="sm"
                  variant={checked ? 'secondary' : 'primary'}
                  onClick={runCheck}
                  loading={check.isPending}
                >
                  {t('admin.tenants.detail.clientDb.check')}
                </Button>
              </div>
              {checkResult && <DbServerChecksList result={checkResult} />}
            </div>
          </Step>

          <Step
            n={5}
            state={s5}
            stateLabel={stateLabel(s5)}
            title={t('admin.tenants.detail.clientDb.step5')}
            testId="client-db-step-5"
          >
            <p className="mb-2 text-xs text-text-secondary">
              {t('admin.tenants.detail.clientDb.step5Hint')}
            </p>
            <Button size="sm" onClick={() => setConfirmOpen(true)} loading={provision.isPending}>
              {t('admin.tenants.detail.clientDb.provision')}
            </Button>
          </Step>
        </ol>
      )}

      <InstallGuideModal open={guideOpen} onClose={() => setGuideOpen(false)} />
      <DbServerFormModal
        open={formOpen}
        server={server}
        defaultName={tenantName}
        onCreated={(created) => linkServer.mutate(created.id)}
        onClose={() => {
          setFormOpen(false);
          if (server)
            void queryClient.invalidateQueries({
              queryKey: [...DB_SERVERS_KEY, 'item', server.id],
            });
        }}
      />
      <PrepareServerModal
        open={prepareOpen}
        server={server}
        onClose={() => {
          setPrepareOpen(false);
          if (server)
            void queryClient.invalidateQueries({
              queryKey: [...DB_SERVERS_KEY, 'item', server.id],
            });
        }}
      />
      <ConfirmDialog
        open={confirmOpen}
        variant="primary"
        title={t('admin.tenants.detail.clientDb.confirmTitle')}
        message={t('admin.tenants.detail.clientDb.confirmMessage', { name: server?.name ?? '' })}
        confirmLabel={t('admin.tenants.detail.clientDb.provision')}
        confirming={provision.isPending}
        onConfirm={() => provision.mutate()}
        onClose={() => setConfirmOpen(false)}
      />
    </Card>
  );
}
